/** Optional metadata for CPT codes (descriptions + dashboard bucket keys). */
import { buildProgramTabsFromEnrolled } from '../../../utils/careProgramUtils';

export const CPT_CODE_META = {
  '99453': { desc: 'Device setup & education', bucketKey: 'cpt99453' },
  '99445': { desc: 'Device supply 2-15 readings/days', bucketKey: 'cpt99445' },
  '99454': { desc: 'Device supply ≥16 readings/days', bucketKey: 'cpt99454' },
  '99470': { desc: 'Monitoring mgmt 10-19 min', bucketKey: 'cpt99470' },
  '99457': { desc: 'Monitoring mgmt ≥20 min', bucketKey: 'cpt99457' },
  '99458': { desc: "Monitoring mgmt add'l 20 min", bucketKey: 'cpt99458' },
  // '99091': { desc: 'Physiologic data interpretation' }, // disabled for now
  '99490': { desc: 'Clinical staff first 20 min', bucketKey: 'cpt99490' },
  '99491': { desc: 'Physician time first 30 min', bucketKey: 'cpt99491' },
  '99439': { desc: "Clinical staff add'l 20 min", bucketKey: 'cpt99439' },
  '99487': { desc: 'Complex CCM first 60 min', bucketKey: 'cpt99487' },
  '99489': { desc: "Complex CCM add'l 30 min", bucketKey: 'cpt99489' },
  '99437': { desc: "Physician add'l 30 min", bucketKey: 'cpt99437' },
  '99424': { desc: 'Physician first 30 min', bucketKey: 'cpt99424' },
  '99425': { desc: "Physician add'l 30 min", bucketKey: 'cpt99425' },
  '99426': { desc: 'Clinical staff first 30 min', bucketKey: 'cpt99426' },
  '99427': { desc: "Clinical staff add'l 30 min", bucketKey: 'cpt99427' },
  '98975': { desc: 'Device setup initial' },
  '98985': { desc: 'MSK device supply 2-15 days' },
  '98977': { desc: 'MSK device supply ≥16 days' },
  '98976': { desc: 'Respiratory device supply ≥16 days' },
  '98979': { desc: 'Treatment mgmt 10-19 min' },
  '98980': { desc: 'Treatment mgmt ≥20 min' },
  '98981': { desc: "Treatment mgmt add'l 20 min" },
  '93000': { desc: 'EKG tracing and report' },
  '93224': { desc: 'Holter monitor 24-48h' },
  '93225': { desc: 'Holter monitor recording' },
  '93229': { desc: 'Interrogation device evaluation' },
  '93294': { desc: 'Interrogation device evaluation' },
  '93015': { desc: 'Cardiovascular stress test' }
};

export const KPI_FILTERS = {
  recentUploads: { label: 'Recent uploads', sub: 'in last 24 hours' },
  missedUploads: { label: 'Missed uploads', sub: 'more than 2 days' },
  abnormalMeasurements: { label: 'Abnormal readings', sub: 'in last 24 hours' }
};

/** Parse cpt_codes from API (JSON array or comma-separated string). */
export function parseCptCodes(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((code) => String(code).trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((code) => String(code).trim()).filter(Boolean);
      }
    } catch {
      // fall through to comma-separated
    }
    return trimmed.split(',').map((code) => code.trim()).filter(Boolean);
  }
  return [];
}

function definitionFromCode(code) {
  const meta = CPT_CODE_META[code] || {};
  return {
    code,
    desc: meta.desc || `CPT ${code}`,
    bucketKey: meta.bucketKey || null
  };
}

function normalizeProgramKey(name) {
  if (!name) return '';
  return String(name).trim().toLowerCase().replace(/\s+/g, '-');
}

/** CPT codes assigned to the practice program only (from practice_care_programs / API). */
export function resolveCptCodesForTab(tab) {
  return parseCptCodes(tab?.cpt_codes);
}

/** Build program tabs preserving only API-assigned cpt_codes per enrolled program. */
export function buildProgramTabsWithCpt(enrolledPrograms) {
  const enrolled = Array.isArray(enrolledPrograms) ? enrolledPrograms : [];

  return buildProgramTabsFromEnrolled(enrolled).map((tab) => {
    const source = enrolled.find((prog) => {
      const shortName = typeof prog === 'string' ? prog : (prog.short_name || prog.id);
      return normalizeProgramKey(shortName) === tab.id;
    });
    const programObj = typeof source === 'string' ? { short_name: source } : source;
    return {
      ...tab,
      cpt_codes: parseCptCodes(programObj?.cpt_codes)
    };
  });
}

/** Build CPT tile definitions for the active enrolled program tab. */
export function resolveProgramCptDefinitions(programTabs, activeProgramTab) {
  const tabs = Array.isArray(programTabs) ? programTabs : [];
  if (tabs.length === 0) return [];

  const tabId = String(activeProgramTab || tabs[0]?.id || '').trim();
  const tab = tabs.find((t) => t.id === tabId) || tabs[0];
  return resolveCptCodesForTab(tab).map(definitionFromCode);
}

/** Attach eligible / ineligible counts to CPT definitions. */
export function buildCptTilesWithCounts(definitions, dashboardBuckets, totalPatients) {
  const total = Number(totalPatients) || 0;
  const buckets = dashboardBuckets || {};

  return (Array.isArray(definitions) ? definitions : []).map((item) => {
    const bucketVal = item.bucketKey ? buckets[item.bucketKey] : null;
    const eligible = typeof bucketVal === 'number'
      ? bucketVal
      : (bucketVal?.length ?? 0);
    return {
      ...item,
      eligible,
      ineligible: Math.max(0, total - eligible)
    };
  });
}
